import React, { useEffect, useState } from 'react';
import { getProjectProgress } from '../services/projectService';
import WeightGauge from './WeightGauge';

const fmt = (n, d = 0) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '-';
  return Number(n).toLocaleString('en-IN', { maximumFractionDigits: d, minimumFractionDigits: d });
};

const ProjectProgressPanel = ({ projectId, month, startDate, endDate }) => {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [includeSubProjects, setIncludeSubProjects] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    getProjectProgress(projectId, { month, startDate, endDate, includeSubProjects })
      .then((res) => setProgress(res.data))
      .catch(() => setProgress(null))
      .finally(() => setLoading(false));
  }, [projectId, month, startDate, endDate, includeSubProjects]);

  if (loading) {
    return <p className="text-sm text-gray-500">Loading progress...</p>;
  }

  if (!progress) {
    return <p className="text-sm text-gray-500">Progress data unavailable.</p>;
  }

  const {
    categories, worker_breakdown: workerBreakdown, total_man_hours: totalManHours,
    // total_cost_to_date is labor-only (DailyReport.total_salary_cost) -
    // kept as its own figure, but all_in_cost_to_date (labor + approved
    // SiteExpense + VendorBill + MaterialPurchase, added 2026-09-11) is
    // what actually belongs next to budget. Comparing labor-only cost
    // against a budget meant to cover material/site spend too made a
    // project look way under budget while its real all-in spend could
    // already be over - see project_routes.py's get_project_progress.
    total_cost_to_date: totalLaborCost, expense_cost_to_date: totalExpenseCost,
    all_in_cost_to_date: totalCost, total_rework_hours: totalRework, start_date: rangeStart,
    sub_projects: subProjects, total_budgeted_cost: totalBudgetedCost,
  } = progress;

  return (
    <div className="space-y-4">
      {subProjects && subProjects.length > 0 && (
        <div className="bg-blue-50 border border-blue-100 rounded p-3 flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs text-blue-800">
            This project has {subProjects.length} sub-project{subProjects.length === 1 ? '' : 's'}:{' '}
            {subProjects.map((sp) => sp.name).join(', ')}
          </div>
          <label className="flex items-center gap-1.5 text-xs font-semibold text-blue-800 whitespace-nowrap cursor-pointer">
            <input
              type="checkbox"
              checked={includeSubProjects}
              onChange={(e) => setIncludeSubProjects(e.target.checked)}
            />
            Include sub-projects in totals below
          </label>
        </div>
      )}

      <div className="grid grid-cols-3 gap-4 text-sm bg-gray-50 rounded p-4">
        <div>
          <div className="text-gray-500">Man-hours{rangeStart ? ' (range)' : ' to date'}</div>
          <div className="font-semibold text-lg">{fmt(totalManHours)}</div>
        </div>
        <div>
          <div className="text-gray-500">
            All-in cost{rangeStart ? ' (range)' : ' to date'}
            {totalBudgetedCost ? <span className="text-gray-400"> / ₹{fmt(totalBudgetedCost)} budget</span> : ''}
          </div>
          <div className="font-semibold text-lg">₹{fmt(totalCost)}</div>
          <div className="text-xs text-gray-400">Labor ₹{fmt(totalLaborCost)} + Expenses ₹{fmt(totalExpenseCost)}</div>
        </div>
        <div>
          <div className="text-gray-500">Rework hours</div>
          <div className={`font-semibold text-lg ${totalRework > 0 ? 'text-red-600' : ''}`}>{fmt(totalRework)}</div>
        </div>
      </div>

      {rangeStart && (
        <p className="text-xs text-gray-400 -mt-2">
          Man-hours/cost/output above are for the selected date range. Weight % complete is always cumulative through the "To" date, since checkpoint progress can't go backwards.
        </p>
      )}

      {(!categories || categories.length === 0) && (
        <p className="text-sm text-gray-400">No daily entries logged against this project's tasks yet.</p>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {categories && categories.map((c) => (
          <div key={c.category_id} className="border border-gray-200 rounded p-4">
            <div className="font-semibold text-sm text-gray-900 mb-2">{c.category_name}</div>
            {c.mode === 'production' ? (
              <>
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Weight complete</span>
                  <span>
                    {c.earned_kg === null ? 'kg not set' : `${fmt(c.earned_kg)} kg`} ({c.percent_complete}%)
                  </span>
                </div>
                {c.earned_kg === null && (
                  <p className="text-xs text-amber-600 mb-1">
                    Set a target weight (kg) on this project (Edit Project) to see completed kg.
                  </p>
                )}
                <WeightGauge value={c.percent_complete} />
                <div className="mt-2 space-y-1">
                  {c.tasks.map((t) => (
                    <div key={t.task_id} className="flex justify-between text-xs text-gray-500">
                      <span>{t.name}</span>
                      <span>{t.checkpoint_label || '—'} ({t.max_checkpoint_percent}%)</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="space-y-1">
                {c.output_by_task.length === 0 && <span className="text-xs text-gray-400">No output logged.</span>}
                {c.output_by_task.map((t) => (
                  <div key={t.task_id} className="flex justify-between text-xs">
                    <span className="text-gray-500">{t.name}</span>
                    <span className="font-medium">
                      {t.total_quantity > 0
                        ? `${fmt(t.total_quantity)} ${t.unit || ''}`
                        : `${t.entry_count} ${t.entry_count === 1 ? 'entry' : 'entries'} logged (qty not recorded)`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {workerBreakdown && workerBreakdown.length > 0 && (
        <>
          <div className="text-xs text-gray-400 uppercase pt-2">Workers</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-400 uppercase border-b border-gray-200">
                <th className="py-2">Worker</th>
                <th className="py-2">Man-hours</th>
                <th className="py-2">Rework hours</th>
                <th className="py-2">Rework ratio</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {workerBreakdown.map((w) => (
                <tr key={w.worker_id}>
                  <td className="py-2">{w.worker_name}</td>
                  <td className="py-2">{fmt(w.man_hours)}</td>
                  <td className="py-2">{fmt(w.rework_hours)}</td>
                  <td className="py-2">
                    <span className={`text-xs px-2 py-1 rounded ${w.rework_ratio_pct > 10 ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}>
                      {w.rework_ratio_pct}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default ProjectProgressPanel;
